import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { auth } from '@/lib/firebase';
import { getApiBaseUrl } from '@/lib/api';

interface AllowedEmail {
  id: string;
  email: string;
  addedAt: string;
  addedBy: string;
  note?: string;
}

interface User {
  id: string;
  email: string;
  timezone: string;
  isAdmin: boolean;
  createdAt: string;
}

export default function AdminPage() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [idToken, setIdToken] = useState<string | null>(null);
  const [allowedEmails, setAllowedEmails] = useState<AllowedEmail[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Form state for adding emails
  const [newEmail, setNewEmail] = useState('');
  const [newNote, setNewNote] = useState('');
  const [isAdding, setIsAdding] = useState(false);

  // Check auth state
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        navigate('/login');
        return;
      }

      try {
        const token = await user.getIdToken();
        setIdToken(token);
      } catch {
        navigate('/login');
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  // Check admin status and fetch data
  const fetchData = useCallback(async () => {
    if (!idToken) return;

    try {
      // Check admin status first
      const statusRes = await fetch(`${getApiBaseUrl()}/admin/status`, {
        headers: { Authorization: `Bearer ${idToken}` },
      });

      if (statusRes.status === 403) {
        setIsAdmin(false);
        setError('Access denied. Admin privileges required.');
        setIsLoading(false);
        return;
      }

      if (!statusRes.ok) {
        throw new Error('Failed to check admin status');
      }

      setIsAdmin(true);

      // Fetch allowed emails and users in parallel
      const [emailsRes, usersRes] = await Promise.all([
        fetch(`${getApiBaseUrl()}/admin/allowed-emails`, {
          headers: { Authorization: `Bearer ${idToken}` },
        }),
        fetch(`${getApiBaseUrl()}/admin/users`, {
          headers: { Authorization: `Bearer ${idToken}` },
        }),
      ]);

      if (emailsRes.ok) {
        const data = await emailsRes.json();
        setAllowedEmails(data.allowedEmails);
      }

      if (usersRes.ok) {
        const data = await usersRes.json();
        setUsers(data.users);
      }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to load admin data'
      );
    } finally {
      setIsLoading(false);
    }
  }, [idToken]);

  useEffect(() => {
    if (idToken) {
      fetchData();
    }
  }, [idToken, fetchData]);

  // Add email to allowlist
  const handleAddEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!idToken || !newEmail.trim()) return;

    setIsAdding(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const res = await fetch(`${getApiBaseUrl()}/admin/allowed-emails`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: newEmail.trim(),
          ...(newNote.trim() && { note: newNote.trim() }),
        }),
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to add email');
      }

      setSuccessMessage(`Email ${newEmail} added to allowlist`);
      setNewEmail('');
      setNewNote('');
      fetchData(); // Refresh the list
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add email');
    } finally {
      setIsAdding(false);
    }
  };

  // Remove email from allowlist
  const handleRemoveEmail = async (email: string) => {
    if (!idToken) return;
    if (!confirm(`Remove ${email} from allowlist?`)) return;

    setError(null);
    setSuccessMessage(null);

    try {
      const res = await fetch(
        `${getApiBaseUrl()}/admin/allowed-emails/${encodeURIComponent(email)}`,
        {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${idToken}` },
        }
      );

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to remove email');
      }

      setSuccessMessage(`Email ${email} removed from allowlist`);
      fetchData(); // Refresh the list
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove email');
    }
  };

  const handleSignOut = async () => {
    await signOut(auth);
    navigate('/login');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-600">Loading...</div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md text-center">
          <h1 className="text-2xl font-bold text-red-600 mb-4">
            Access Denied
          </h1>
          <p className="text-gray-600 mb-4">
            You do not have admin privileges to access this page.
          </p>
          <Link
            to="/dashboard"
            className="text-indigo-600 hover:text-indigo-500"
          >
            Go to Dashboard
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/dashboard" className="text-gray-600 hover:text-gray-900">
              &larr; Dashboard
            </Link>
            <h1 className="text-xl font-semibold text-gray-900">Admin Panel</h1>
          </div>
          <button
            onClick={handleSignOut}
            className="text-gray-600 hover:text-gray-900 text-sm"
          >
            Sign Out
          </button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Messages */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            {error}
          </div>
        )}
        {successMessage && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700">
            {successMessage}
          </div>
        )}

        {/* Add Email Form */}
        <section className="bg-white rounded-lg shadow-sm p-6 mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Add Allowed Email
          </h2>
          <form onSubmit={handleAddEmail} className="space-y-4">
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Email Address
              </label>
              <input
                type="email"
                id="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder="user@example.com"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                required
              />
            </div>
            <div>
              <label
                htmlFor="note"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Note (optional)
              </label>
              <input
                type="text"
                id="note"
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                placeholder="e.g., Friend, Beta tester"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <button
              type="submit"
              disabled={isAdding || !newEmail.trim()}
              className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isAdding ? 'Adding...' : 'Add Email'}
            </button>
          </form>
        </section>

        {/* Allowed Emails List */}
        <section className="bg-white rounded-lg shadow-sm p-6 mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Allowed Emails ({allowedEmails.length})
          </h2>
          {allowedEmails.length === 0 ? (
            <p className="text-gray-500">No emails in allowlist yet.</p>
          ) : (
            <div className="space-y-3">
              {allowedEmails.map((item) => (
                <div
                  key={item.id}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-md"
                >
                  <div>
                    <div className="font-medium text-gray-900">
                      {item.email}
                    </div>
                    {item.note && (
                      <div className="text-sm text-gray-500">{item.note}</div>
                    )}
                    <div className="text-xs text-gray-400">
                      Added {new Date(item.addedAt).toLocaleDateString()}
                    </div>
                  </div>
                  <button
                    onClick={() => handleRemoveEmail(item.email)}
                    className="text-red-600 hover:text-red-700 text-sm"
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Users List */}
        <section className="bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Registered Users ({users.length})
          </h2>
          {users.length === 0 ? (
            <p className="text-gray-500">No users registered yet.</p>
          ) : (
            <div className="space-y-3">
              {users.map((user) => (
                <div
                  key={user.id}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-md"
                >
                  <div>
                    <div className="font-medium text-gray-900">
                      {user.email}
                      {user.isAdmin && (
                        <span className="ml-2 px-2 py-0.5 bg-indigo-100 text-indigo-700 text-xs rounded-full">
                          Admin
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-gray-500">{user.timezone}</div>
                    <div className="text-xs text-gray-400">
                      Joined {new Date(user.createdAt).toLocaleDateString()}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
